import { useState } from "react";
import { Check, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

export default function FiltersPanel({
  filters,
  setFilters,
  divisions,
  divisionsByProvince,
  provinces,
  grades,
  interestOptions,
  onApply,
  onReset,
}) {
  const [divisionSearch, setDivisionSearch] = useState("");
  const provinceDivisions = filters.province ? divisionsByProvince[filters.province] || [] : divisions;
  const visibleDivisions = provinceDivisions.filter((division) => division.toLowerCase().includes(divisionSearch.toLowerCase()));
  const toggleListValue = (key, value) => {
    const selected = filters[key].includes(value);
    setFilters({
      ...filters,
      [key]: selected ? filters[key].filter((item) => item !== value) : [...filters[key], value],
    });
  };

  return (
    <Card className="p-5 mb-6">
      <form onSubmit={(event) => { event.preventDefault(); onApply(); }}>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <h2 className="font-medium text-foreground">Filter schools</h2>
            <p className="text-xs text-muted-foreground mt-1">These filters drive both the CRM table and the forecast.</p>
          </div>
          <div className="flex gap-2">
            {hasActiveFilters(filters, divisions) && (
              <Button type="button" variant="ghost" size="sm" onClick={() => { setDivisionSearch(""); onReset(); }}>
                <X className="h-4 w-4" /> Reset
              </Button>
            )}
            <Button type="submit" size="sm">Apply filters</Button>
          </div>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={filters.q}
            onChange={(event) => setFilters({ ...filters, q: event.target.value })}
            placeholder="Search schools, divisions, people, or contact details"
            className="h-10 pl-9 text-sm"
            aria-label="Search schools or contacts"
          />
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          <FilterSection title="Division">
            <LabeledSelect label="Province" value={filters.province || ""} onChange={(province) => setFilters({ ...filters, province: province || null, divisions: [...divisions] })}>
              <option value="">All provinces</option>
              {provinces.map((province) => <option key={province.value} value={province.value}>{province.label}</option>)}
            </LabeledSelect>
            <Input value={divisionSearch} onChange={(event) => setDivisionSearch(event.target.value)} placeholder="Find a division" aria-label="Find a division" className="h-9 text-sm" />
            <div className="flex gap-3 text-xs">
              <button type="button" className="text-primary hover:underline" onClick={() => setFilters({ ...filters, divisions: [...provinceDivisions] })}>Select all</button>
              <button type="button" className="text-primary hover:underline" onClick={() => setFilters({ ...filters, divisions: [] })}>Clear all</button>
            </div>
            <div className="max-h-48 space-y-2 overflow-y-auto pr-2">
              {visibleDivisions.map((division) => (
                <Checkbox
                  key={division}
                  label={division}
                  checked={filters.divisions.includes(division)}
                  onChange={() => toggleListValue("divisions", division)}
                />
              ))}
            </div>
          </FilterSection>

          <FilterSection title="Grades offered" className="xl:col-span-2">
            <div className="grid grid-cols-7 gap-1.5">
              {grades.map((grade) => (
                <button
                  key={grade}
                  type="button"
                  onClick={() => toggleListValue("grades", grade)}
                  className={`h-8 rounded-md border text-xs font-medium transition-colors ${filters.grades.includes(grade) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground"}`}
                  aria-pressed={filters.grades.includes(grade)}
                  aria-label={`Grade ${grade}`}
                >
                  {grade}
                </button>
              ))}
            </div>
            <select
              value={filters.grade_mode}
              onChange={(event) => setFilters({ ...filters, grade_mode: event.target.value })}
              className="mt-2 h-9 w-full rounded-md border border-input bg-card px-2 text-xs"
              aria-label="Grade matching mode"
              disabled={filters.grades.length === 0}
            >
              <option value="any">Offers any selected grade</option>
              <option value="all">Offers all selected grades</option>
              <option value="only">Offers only selected grades</option>
            </select>
          </FilterSection>

          <FilterSection title="Outreach data">
            <Checkbox
              label="Has school council contact"
              checked={filters.requires_council}
              onChange={(checked) => setFilters({ ...filters, requires_council: checked })}
            />
            <Checkbox
              label="Has parent society contact"
              checked={filters.requires_society}
              onChange={(checked) => setFilters({ ...filters, requires_society: checked })}
            />
          </FilterSection>

          <FilterSection title="Pipeline">
            <LabeledSelect label="Lead status" value={filters.pipeline} onChange={(value) => setFilters({ ...filters, pipeline: value })}>
              <option value="all">All schools</option>
              <option value="possible">Possible leads only</option>
              <option value="not_possible">Not yet a possible lead</option>
            </LabeledSelect>
          </FilterSection>

          <FilterSection title="CRM status">
            <LabeledSelect label="Contacted" value={filters.contacted} onChange={(value) => setFilters({ ...filters, contacted: value })}>
              <option value="all">Any contact status</option>
              <option value="yes">Contacted</option>
              <option value="no">Not contacted</option>
            </LabeledSelect>
            <LabeledSelect label="Interest" value={filters.interest_status || ""} onChange={(value) => setFilters({ ...filters, interest_status: value || null })}>
              <option value="">Any interest</option>
              {interestOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </LabeledSelect>
          </FilterSection>

          <FilterSection title="Enrollment range" className="md:col-span-2">
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="number"
                min="0"
                value={filters.min_enrollment ?? ""}
                onChange={(event) => setFilters({ ...filters, min_enrollment: event.target.value })}
                placeholder="Minimum"
                aria-label="Minimum enrollment"
                className="h-9 px-3 text-sm"
              />
              <Input
                type="number"
                min="0"
                value={filters.max_enrollment ?? ""}
                onChange={(event) => setFilters({ ...filters, max_enrollment: event.target.value })}
                placeholder="Maximum"
                aria-label="Maximum enrollment"
                className="h-9 px-3 text-sm"
              />
            </div>
          </FilterSection>
        </div>
      </form>
    </Card>
  );
}

function FilterSection({ title, className = "", children }) {
  return <section className={className}><h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{title}</h3><div className="space-y-2">{children}</div></section>;
}

function Checkbox({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${checked ? "border-primary bg-primary text-primary-foreground" : "border-input bg-card"}`}>
        {checked && <Check className="h-3 w-3" />}
      </span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="sr-only" />
      {label}
    </label>
  );
}

function LabeledSelect({ label, value, onChange, children }) {
  return (
    <label className="block">
      <span className="sr-only">{label}</span>
      <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value)} className="h-9 w-full rounded-md border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {children}
      </select>
    </label>
  );
}

function hasActiveFilters(filters, divisions) {
  return Boolean(
    filters.q
    || filters.province
    || filters.divisions.length !== divisions.length
    || filters.grades.length > 0
    || filters.requires_council
    || filters.requires_society
    || filters.pipeline !== "all"
    || filters.min_enrollment !== null
    || filters.max_enrollment !== null
    || filters.contacted !== "all"
    || filters.interest_status,
  );
}
